import { Component } from '@theme/component';
import { trapFocus, removeTrapFocus } from '@theme/focus';

/**
 * A custom element that manages the main menu drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDetailsElement} details - The details element.
 *
 * @extends {Component<Refs>}
 */
class HeaderDrawer extends Component {
  requiredRefs = ['details'];
  #pendingPointerAction = null;
  #pointerFallbackDelay = 150;

  connectedCallback() {
    super.connectedCallback();

    this.addEventListener('pointerdown', this.#onPointerDown, true);
    this.addEventListener('pointerup', this.#onPointerUp, true);
    this.addEventListener('pointercancel', this.#onPointerCancel, true);
    this.addEventListener('click', this.#onClickCapture, true);
    this.addEventListener('toggle', this.#onToggle);
    this.addEventListener('keyup', this.#onKeyUp);
    this.#setupAnimatedElementListeners();

    const { details } = this.refs;
    if (details?.open) {
      this.#syncOpenState(details);
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.removeEventListener('pointerdown', this.#onPointerDown, true);
    this.removeEventListener('pointerup', this.#onPointerUp, true);
    this.removeEventListener('pointercancel', this.#onPointerCancel, true);
    this.removeEventListener('click', this.#onClickCapture, true);
    this.removeEventListener('toggle', this.#onToggle);
    this.removeEventListener('keyup', this.#onKeyUp);
    this.#clearPendingPointerAction();
  }

  #onPointerDown = (event) => {
    this.#armPointerFallback(event);
  };

  #onPointerUp = (event) => {
    this.#schedulePointerFallback(event);
  };

  #onPointerCancel = () => {
    this.#clearPendingPointerAction();
  };

  #onClickCapture = () => {
    this.#clearPendingPointerAction();
  };

  /**
   * Close the main menu drawer when the Escape key is pressed
   * @param {KeyboardEvent} event
   */
  #onKeyUp = (event) => {
    if (event.key !== 'Escape') return;

    this.#close(this.#getDetailsElement(event));
  };

  /**
   * Keep classes, aria and focus in sync with the native details state.
   * @param {Event} event
   */
  #onToggle = (event) => {
    const details = this.#getDetailsElement(event);
    if (!(details instanceof HTMLDetailsElement)) return;

    if (details.open) {
      this.#syncOpenState(details);
    } else {
      this.#syncClosedState(details);
    }
  };

  /**
   * @returns {boolean} Whether the main menu drawer is open
   */
  get isOpen() {
    return this.refs.details.hasAttribute('open');
  }

  /**
   * Get the closest details element to the event target
   * @param {Event | {target?: EventTarget} | undefined} event
   * @returns {HTMLDetailsElement}
   */
  #getDetailsElement(event) {
    if (!(event?.target instanceof Element)) return this.refs.details;

    return event.target.closest('details') ?? this.refs.details;
  }

  /**
   * @param {EventTarget | null} target
   * @returns {Element | null}
   */
  #getControlElement(target) {
    if (!(target instanceof Element)) return null;

    const control = target.closest(
      'summary, .menu-drawer__close-button, .menu-drawer__backdrop, .js-open-submenu, .js-close-submenu, a, button'
    );

    if (!control || !this.contains(control)) return null;
    return control;
  }

  /**
   * @param {Element} control
   * @returns {'summary' | 'close-button' | 'backdrop' | 'open-submenu' | 'close-submenu' | 'link' | 'interactive'}
   */
  #getControlRole(control) {
    if (control.matches('summary')) return 'summary';
    if (control.matches('.menu-drawer__close-button')) return 'close-button';
    if (control.matches('.menu-drawer__backdrop')) return 'backdrop';
    if (control.matches('.js-open-submenu')) return 'open-submenu';
    if (control.matches('.js-close-submenu')) return 'close-submenu';
    if (control.matches('a')) return 'link';

    return 'interactive';
  }

  /**
   * @param {string} role
   * @returns {boolean}
   */
  #shouldUsePointerFallback(role) {
    return role === 'summary' || role === 'close-button' || role === 'backdrop';
  }

  /**
   * Safari on touch devices can swallow the first click on summary-like controls.
   * Track the gesture and emulate the action if no click follows.
   * @param {PointerEvent} event
   */
  #armPointerFallback(event) {
    if (!(event instanceof PointerEvent)) return;
    if (event.pointerType === 'mouse' || event.button !== 0) return;

    const control = this.#getControlElement(event.target);
    if (!control) return;

    const controlRole = this.#getControlRole(control);
    if (!this.#shouldUsePointerFallback(controlRole)) return;

    this.#clearPendingPointerAction();
    this.#pendingPointerAction = {
      control,
      controlRole,
      pointerId: event.pointerId,
      timeoutId: null,
    };
  }

  /**
   * @param {PointerEvent} event
   */
  #schedulePointerFallback(event) {
    if (!(event instanceof PointerEvent)) return;

    const pending = this.#pendingPointerAction;
    if (!pending || pending.pointerId !== event.pointerId) return;

    const control = this.#getControlElement(event.target);
    if (!control || control !== pending.control) {
      this.#clearPendingPointerAction();
      return;
    }

    if (pending.timeoutId) {
      window.clearTimeout(pending.timeoutId);
    }

    pending.timeoutId = window.setTimeout(() => {
      if (this.#pendingPointerAction !== pending) return;

      this.#pendingPointerAction = null;
      this.#runPointerFallback(pending.control, pending.controlRole);
    }, this.#pointerFallbackDelay);
  }

  /**
   * @param {Element} control
   * @param {string} controlRole
   */
  #runPointerFallback(control, controlRole) {
    if (!(control instanceof Element) || !document.contains(control)) return;

    if (controlRole === 'summary') {
      const details = control.closest('details');
      if (!(details instanceof HTMLDetailsElement)) return;

      if (details.open) {
        this.#close(details);
      } else {
        this.open({ target: control });
      }

      return;
    }

    if (controlRole === 'close-button' || controlRole === 'backdrop') {
      this.close();
    }
  }

  #clearPendingPointerAction() {
    const pending = this.#pendingPointerAction;
    if (!pending) return;

    if (pending.timeoutId) {
      window.clearTimeout(pending.timeoutId);
    }

    this.#pendingPointerAction = null;
  }

  /**
   * Toggle the main menu drawer
   */
  toggle() {
    return this.isOpen ? this.close() : this.open();
  }

  /**
   * Open the closest drawer or the main menu drawer
   * @param {Event | {target?: EventTarget}} [event]
   */
  open(event) {
    const details = this.#getDetailsElement(event);
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'true');

    if (!(event instanceof Event)) {
      details.open = true;
    }

    this.#syncOpenState(details);
  }

  /**
   * Go back or close the main menu drawer
   * @param {Event} [event]
   */
  back(event) {
    this.#close(this.#getDetailsElement(event));
  }

  /**
   * Close the main menu drawer
   */
  close() {
    this.#close(this.refs.details);
  }

  /**
   * Close the closest menu or submenu that is open
   *
   * @param {HTMLDetailsElement} details
   */
  #close(details) {
    const summary = details.querySelector('summary');

    if (!summary) return;

    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');
    details.open = false;

    if (details === this.refs.details) {
      removeTrapFocus();

      const openDetails = this.querySelectorAll('details[open]');
      openDetails.forEach(reset);
    } else if (this.refs.details.open) {
      setTimeout(() => {
        trapFocus(this.refs.details);
      }, 0);
    }
  }

  /**
   * Attach animationend event listeners to all animated elements to remove will-change after animation
   * to remove the stacking context and allow submenus to be positioned correctly
   */
  #setupAnimatedElementListeners() {
    const removeWillChangeOnAnimationEnd = (event) => {
      const target = event.target;
      if (target && target instanceof HTMLElement) {
        target.style.setProperty('will-change', 'unset');
        target.removeEventListener('animationend', removeWillChangeOnAnimationEnd);
      }
    };

    const allAnimated = this.querySelectorAll('.menu-drawer__animated-element');
    allAnimated.forEach((element) => {
      element.addEventListener('animationend', removeWillChangeOnAnimationEnd);
    });
  }

  /**
   * @param {HTMLDetailsElement} details
   */
  #syncOpenState(details) {
    const summary = details.querySelector('summary');
    if (!summary) return;

    summary.setAttribute('aria-expanded', 'true');

    requestAnimationFrame(() => {
      if (!details.open) return;

      details.classList.add('menu-open');

      if (details === this.refs.details) {
        setTimeout(() => {
          if (!details.open) return;
          trapFocus(details);
        }, 0);
      }
    });
  }

  /**
   * @param {HTMLDetailsElement} details
   */
  #syncClosedState(details) {
    const summary = details.querySelector('summary');
    if (!summary) return;

    summary.setAttribute('aria-expanded', 'false');
    details.classList.remove('menu-open');

    if (details === this.refs.details) {
      removeTrapFocus();

      const openDetails = this.querySelectorAll('details[open]');
      openDetails.forEach(reset);
    } else if (this.refs.details.open) {
      setTimeout(() => {
        trapFocus(this.refs.details);
      }, 0);
    }
  }
}

if (!customElements.get('header-drawer')) {
  customElements.define('header-drawer', HeaderDrawer);
}

/**
 * Reset an open details element to its original state
 *
 * @param {HTMLDetailsElement} element
 */
function reset(element) {
  element.classList.remove('menu-open');
  element.removeAttribute('open');
  element.querySelector('summary')?.setAttribute('aria-expanded', 'false');
}
